/**
 * Shared funding-mechanism comparison helpers for the Suggestion Engines.
 *
 * The whole point of a Suggestion Engine is to look at the realistic ways to fund a cash outlay and let the
 * projected outcome decide — accounting for liquidation costs (CGT on shares) AND the return you give up on
 * each asset. A pensioner should usually draw a tax-free account-based pension (ABP) before selling shares and
 * realising CGT — but not always (preserving a tax-free, compounding ABP can win when the CGT is small). So we
 * generate BOTH paths as candidates and let the engine rank them, rather than hard-coding a single draw order.
 *
 * Used by the granny-flat, retirement-village and downsizer modules. Funding maps use the same `type-id` → gross
 * amount shape the manual asset pickers / liquidateChosenAssets use.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { AssetType } from '../../../shared/src/calculation/factFindTypes.ts';

/** `type-id` → gross amount to draw from that asset. */
export type FundingMap = Record<string, number>;

export interface FundingVariant {
    key: string;
    nameSuffix: string;
    note: string;
    funding: FundingMap;
}

/** Total value held in a liquid asset type. */
export const sumAssetType = (factFind: DraftFactFind | null | undefined, type: AssetType | string): number =>
    (factFind?.assets?.[type] || []).reduce((s, a) => s + (a.value || 0), 0);

/** Build a chosen-asset funding map raising `target` (gross) by drawing the given types in order. */
export function drawFundingMap(
    factFind: DraftFactFind | null | undefined,
    target: number,
    typeOrder: (AssetType | string)[],
    reserve = 0,
): FundingMap {
    let remaining = target || 0;
    const map: FundingMap = {};
    (typeOrder || []).forEach((type) => {
        if (remaining <= 0) return;
        const assets = factFind?.assets?.[type] || [];
        if (type === 'bankAccounts' && reserve > 0) {
            // Keep an emergency reserve in the bank — only the amount above the reserve is available to draw.
            const totalBank = assets.reduce((s, a) => s + (a.value || 0), 0);
            let avail = Math.max(0, totalBank - reserve);
            assets.forEach((a) => {
                if (remaining <= 0 || avail <= 0) return;
                const val = a.value || 0;
                const take = Math.min(val, remaining, avail);
                if (take > 0) { map[`${type}-${a.id}`] = Math.round(take); remaining -= take; avail -= take; }
            });
            return;
        }
        assets.forEach((a) => {
            if (remaining <= 0) return;
            const val = a.value || 0;
            const take = Math.min(val, remaining);
            if (take > 0) { map[`${type}-${a.id}`] = Math.round(take); remaining -= take; }
        });
    });
    return map;
}

/**
 * Distinct, sensible ways to fund a cash outlay of `target` from liquid assets. Returns ONE variant when there's
 * no real choice (cash/term covers it, or the client holds only one of shares / ABP), else TWO to compare:
 * draw the tax-free ABP (no CGT) vs sell shares (CGT, preserves the ABP). Each variant: { key, nameSuffix, note,
 * funding } where `funding` is a `type-id`→amount map (empty ⇒ "draw automatically").
 */
export function buildFundingVariants(
    factFind: DraftFactFind | null | undefined,
    target: number,
    reserve = 0,
): FundingVariant[] {
    const cash = ['bankAccounts', 'termDeposits'];
    const cashAvail = Math.max(0, sumAssetType(factFind, 'bankAccounts') - reserve) + sumAssetType(factFind, 'termDeposits');
    const hasShares = sumAssetType(factFind, 'shares') > 0;
    const hasAbp = sumAssetType(factFind, 'accountBasedPensions') > 0;
    if (cashAvail >= (target || 0) || !(hasShares && hasAbp)) {
        return [{ key: '', nameSuffix: '', note: '', funding: drawFundingMap(factFind, target, [...cash, 'accountBasedPensions', 'shares'], reserve) }];
    }
    return [
        {
            key: '-abp',
            nameSuffix: ' — drawn from your account-based pension',
            note: 'Draws the tax-free account-based pension instead of selling shares, so no CGT — the trade-off is spending down the tax-free pension.',
            funding: drawFundingMap(factFind, target, [...cash, 'accountBasedPensions', 'shares'], reserve),
        },
        {
            key: '-shares',
            nameSuffix: ' — funded by selling shares',
            note: 'Sells shares (realising CGT) and preserves the tax-free account-based pension — better when the CGT is small and the pension would keep compounding tax-free.',
            funding: drawFundingMap(factFind, target, [...cash, 'shares', 'accountBasedPensions'], reserve),
        },
    ];
}
